import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { transportPaymentsService } from "@/services/system-admin/transport-payments.service";
import type {
  TransportPaymentsState,
  GetPaymentsParams,
  CreatePaymentLinkRequest,
  CancelPaymentLinkRequest,
  VerifyPaymentRequest,
  RecordOfflinePaymentRequest,
} from "@/types/admin/transport-payment-type";

const initialState: TransportPaymentsState = {
  payments: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,

  paymentDetail: null,
  detailLoading: false,

  paymentLinks: [],
  paymentLinksLoading: false,

  dashboard: null,
  dashboardLoading: false,

  actionLoading: false,
};

export const fetchPayments = createAsyncThunk(
  "transportPayments/fetchPayments",
  async (params: GetPaymentsParams) => {
    const response = await transportPaymentsService.getPayments(params);
    return {
      ...response,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
  },
);

export const fetchPaymentDetail = createAsyncThunk(
  "transportPayments/fetchPaymentDetail",
  async (paymentUid: string) => transportPaymentsService.getPaymentById(paymentUid),
);

export const verifyPayment = createAsyncThunk(
  "transportPayments/verifyPayment",
  async ({
    paymentUid,
    payload,
  }: {
    paymentUid: string;
    payload: VerifyPaymentRequest;
  }) => transportPaymentsService.verifyPayment(paymentUid, payload),
);

export const fetchPaymentLinks = createAsyncThunk(
  "transportPayments/fetchPaymentLinks",
  async (bookingUid: string) => transportPaymentsService.getPaymentLinks(bookingUid),
);

export const createPaymentLink = createAsyncThunk(
  "transportPayments/createPaymentLink",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: CreatePaymentLinkRequest;
  }) => transportPaymentsService.createPaymentLink(bookingUid, payload),
);

export const regeneratePaymentLink = createAsyncThunk(
  "transportPayments/regeneratePaymentLink",
  async (bookingUid: string) =>
    transportPaymentsService.regeneratePaymentLink(bookingUid),
);

export const cancelPaymentLink = createAsyncThunk(
  "transportPayments/cancelPaymentLink",
  async ({
    paymentUid,
    payload,
  }: {
    paymentUid: string;
    payload: CancelPaymentLinkRequest;
  }) => transportPaymentsService.cancelPaymentLink(paymentUid, payload),
);

export const recordOfflinePayment = createAsyncThunk(
  "transportPayments/recordOfflinePayment",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: RecordOfflinePaymentRequest;
  }) => transportPaymentsService.recordOfflinePayment(bookingUid, payload),
);

export const fetchTransportDashboard = createAsyncThunk(
  "transportPayments/fetchTransportDashboard",
  async () => transportPaymentsService.getDashboard(),
);

const transportPaymentsSlice = createSlice({
  name: "transportPayments",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearPaymentDetail: (state) => {
      state.paymentDetail = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch payments.";
      })

      // ---- detail ----
      .addCase(fetchPaymentDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.paymentDetail = action.payload;
      })
      .addCase(fetchPaymentDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || "Failed to fetch payment.";
      })

      // ---- payment links ----
      .addCase(fetchPaymentLinks.pending, (state) => {
        state.paymentLinksLoading = true;
      })
      .addCase(fetchPaymentLinks.fulfilled, (state, action) => {
        state.paymentLinksLoading = false;
        state.paymentLinks = action.payload;
      })
      .addCase(fetchPaymentLinks.rejected, (state) => {
        state.paymentLinksLoading = false;
      })

      // ---- dashboard ----
      .addCase(fetchTransportDashboard.pending, (state) => {
        state.dashboardLoading = true;
      })
      .addCase(fetchTransportDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchTransportDashboard.rejected, (state) => {
        state.dashboardLoading = false;
      })

      // ---- generic action matchers (verify/createLink/regenerate/cancelLink/offline) ----
      .addMatcher(
        (action) =>
          action.type.startsWith("transportPayments/") &&
          action.type.endsWith("/pending") &&
          !action.type.includes("fetchPayments") &&
          !action.type.includes("fetchPaymentDetail") &&
          !action.type.includes("fetchPaymentLinks") &&
          !action.type.includes("fetchTransportDashboard"),
        (state) => {
          state.actionLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("transportPayments/") &&
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("fetchPayments") &&
          !action.type.includes("fetchPaymentDetail") &&
          !action.type.includes("fetchPaymentLinks") &&
          !action.type.includes("fetchTransportDashboard"),
        (state) => {
          state.actionLoading = false;
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("transportPayments/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("fetchPayments") &&
          !action.type.includes("fetchPaymentDetail") &&
          !action.type.includes("fetchPaymentLinks") &&
          !action.type.includes("fetchTransportDashboard"),
        (state, action: any) => {
          state.actionLoading = false;
          state.error = action.error?.message || "Action failed.";
        },
      );
  },
});

export const { setPage, setPageSize, clearPaymentDetail, clearError } =
  transportPaymentsSlice.actions;
export default transportPaymentsSlice.reducer;